import { useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';
import { useAuthStore, type AuthUser } from '../../../store/authStore';
import { getMyProfile, type ProfileDto } from '../../../api/users';
import { ProfileSection } from '../../../pages/SettingsPage/ProfileSection';
import { AvatarUploader } from '../../AvatarUploader';
import { useAvatarEffectStore, AVATAR_EFFECTS, type AvatarEffect } from '../../../store/avatarEffectStore';
import { useWorkStatusStore } from '../../../store/workStatusStore';
import { Building2, Mail, Key, User } from '../../../icons';
import {
  tSettings, getSettingsLang, subscribeSettingsLang, type SettingsI18nKey,
} from '../../../lib/settings-i18n';
import { formatDate } from '../../../lib/base-i18n';
import { tLayout } from '../../../lib/layout-i18n';
import './ProfilePanel.css';

/**
 * 프로필 (2026-08-20 재설계) — 히어로 + 카드 넷.
 *
 * 왜 바꿨나: 지금까지 이 화면에는 **이름·직책·전화** 셋뿐이었는데, 서버는 그보다 훨씬 많이
 * 주고 있었다(권한·가입일·마지막 로그인·계정 상태). 회사·부서·팀·사번도 데이터베이스에는
 * 있었지만 화면이 받지 못했다 — 그래서 `/users/me` 가 그 둘을 함께 주도록 넓혔다(마이그레이션 0).
 *
 * 편집 폼은 **기존 `ProfileSection` 을 그대로** 쓴다(`variant='fields-only'` 로 히어로만 뺀다).
 * 저장·검증 로직을 복제하지 않기 위해서다. 나머지 카드는 읽기 전용이다.
 *
 * 값이 없는 줄은 아예 그리지 않는다 — 구 서버(필드 미제공)에서도 화면이 깨지지 않는다.
 */

const ROLE_LABEL_KEY: Record<AuthUser['role'], SettingsI18nKey> = {
  STAFF: 'settings.profile.role_staff',
  MANAGER: 'settings.profile.role_manager',
  ADMIN: 'settings.profile.role_admin',
  SYSTEM_ADMIN: 'settings.profile.role_system_admin',
};

export function ProfilePanel() {
  useSyncExternalStore(subscribeSettingsLang, getSettingsLang, getSettingsLang);
  const user = useAuthStore((s) => s.user);
  const [profile, setProfile] = useState<ProfileDto | null>(null);

  useEffect(() => {
    let cancelled = false;
    getMyProfile().then((p) => { if (!cancelled) setProfile(p); }).catch(() => { /* best-effort */ });
    return () => { cancelled = true; };
  }, []);

  /* 사진 자체는 AvatarUploader 가 이미 서버에 올린다(업로드/삭제 전용 엔드포인트).
     여기서는 화면 상태만 맞춘다 — 전 화면 아바타가 authStore 를 보고 그려지기 때문이다. */
  function onAvatarChange(next: string | null) {
    setProfile((prev) => (prev ? { ...prev, avatarUrl: next } : prev));
    useAuthStore.setState((prev) => ({
      ...prev,
      user: prev.user ? { ...prev.user, avatarUrl: next } : null,
    }));
  }

  if (!user) return null;

  return (
    <>
      <ProfileHero user={user} profile={profile} onAvatarChange={onAvatarChange} />

      <div className="pf-cards">
        <OrgCard profile={profile} />
        <AccountCard profile={profile} />
        <IntroCard profile={profile} />
      </div>

      {/* 편집 폼 — 기존 화면 그대로(히어로만 뺐다). */}
      <ProfileSection variant="fields-only" />
    </>
  );
}

/* ── 히어로 ── */
function ProfileHero({ user, profile, onAvatarChange }: {
  user: AuthUser;
  profile: ProfileDto | null;
  onAvatarChange: (next: string | null) => void;
}) {
  const effect = useAvatarEffectStore((s) => s.effect);
  const workStatus = useWorkStatusStore((s) => s.status);
  const [picking, setPicking] = useState(false);

  return (
    <section className="settings-section pf-hero" aria-label={tSettings('settings.profile.photo_heading')}>
      <div className="pf-hero__top">
        <span className={`pf-av pf-av--${effect}`}>
          <AvatarUploader
            userId={user.id}
            name={profile?.name ?? user.name}
            value={profile?.avatarUrl ?? user.avatarUrl ?? null}
            onChange={onAvatarChange}
            readOnly /* 2026-09-13 — 사진은 인사에서 관리자가 고친다 */
          />
        </span>

        <div className="pf-hero__meta">
          <div className="pf-hero__name">
            {profile?.name ?? user.name}
            <span className="settings-role-badge">{tSettings(ROLE_LABEL_KEY[user.role])}</span>
          </div>
          <div className="pf-hero__mail"><Mail size={14} aria-hidden />{profile?.email ?? user.email}</div>
          <div className="pf-hero__tags">
            {workStatus && <span className="pf-tag pf-tag--work">{workStatusLabel(workStatus)}</span>}
            {profile?.employeeNumber && (
              <span className="pf-tag">{tSettings('settings.profile.employee_no')} {profile.employeeNumber}</span>
            )}
            {profile?.org && (
              <span className="pf-tag">
                {profile.org.teamName}
                {' · '}
                {tSettings(profile.org.isLeader ? 'settings.profile.team_leader' : 'settings.profile.team_member')}
              </span>
            )}
          </div>
        </div>

        <button type="button" className="pf-hero__fx" onClick={() => setPicking((v) => !v)}>
          {tSettings('settings.profile.effect_pick')}
        </button>
      </div>

      {picking && <EffectPicker />}
    </section>
  );
}

const EFFECT_LABEL: Record<AvatarEffect, SettingsI18nKey> = {
  none: 'settings.profile.fx.none',
  status: 'settings.profile.fx.status',
  ring: 'settings.profile.fx.ring',
  spin: 'settings.profile.fx.spin',
  pulse: 'settings.profile.fx.pulse',
  glow: 'settings.profile.fx.glow',
  frame: 'settings.profile.fx.frame',
};

function EffectPicker() {
  const effect = useAvatarEffectStore((s) => s.effect);
  const setEffect = useAvatarEffectStore((s) => s.setEffect);
  const user = useAuthStore((s) => s.user);
  const initial = (user?.name ?? '?').trim().slice(0, 1);

  return (
    <div className="pf-fx" role="radiogroup" aria-label={tSettings('settings.profile.effect_pick')}>
      {AVATAR_EFFECTS.map((key) => {
        const label = tSettings(EFFECT_LABEL[key]);
        const on = effect === key;
        return (
          <button
            key={key}
            type="button"
            role="radio"
            aria-checked={on}
            aria-label={label}
            className={`pf-fx__item${on ? ' is-on' : ''}`}
            onClick={() => setEffect(key)}
          >
            <span className={`pf-av pf-av--${key} pf-av--sm`}>
              <span className="pf-av__demo">{initial}</span>
            </span>
            <span className="pf-fx__label">{label}</span>
          </button>
        );
      })}
      <p className="pf-fx__note">{tSettings('settings.profile.effect_note')}</p>
    </div>
  );
}

/* ── 카드 ── */
function Card({ title, icon, children }: { title: string; icon: ReactNode; children: ReactNode }) {
  return (
    <section className="settings-section pf-card" aria-label={title}>
      <h2 className="pf-card__h">{icon}{title}</h2>
      <dl className="pf-lines">{children}</dl>
    </section>
  );
}

function Line({ k, v }: { k: string; v: string | null | undefined }) {
  if (!v) return null;
  return (
    <div className="pf-line">
      <dt>{k}</dt>
      <dd>{v}</dd>
    </div>
  );
}

function OrgCard({ profile }: { profile: ProfileDto | null }) {
  return (
    <Card title={tSettings('settings.profile.card_org')} icon={<Building2 size={14} aria-hidden />}>
      <Line k={tSettings('settings.profile.company')} v={profile?.institutionName} />
      <Line k={tSettings('settings.profile.division')} v={profile?.org?.divisionName} />
      <Line k={tSettings('settings.profile.team')} v={profile?.org?.teamName} />
      <Line k={tSettings('settings.profile.label_position')} v={profile?.position ?? null} />
      <Line k={tSettings('settings.profile.employee_no')} v={profile?.employeeNumber} />
      <Line
        k={tSettings('settings.profile.team_joined')}
        v={profile?.org?.joinedAt ? formatDate(profile.org.joinedAt) : null}
      />
      {!profile?.institutionName && !profile?.org && (
        <p className="pf-empty">{tSettings('settings.profile.org_empty')}</p>
      )}
    </Card>
  );
}

function AccountCard({ profile }: { profile: ProfileDto | null }) {
  return (
    <Card title={tSettings('settings.profile.card_account')} icon={<Key size={14} aria-hidden />}>
      <Line
        k={tSettings('settings.profile.joined_at')}
        v={profile?.createdAt ? formatDate(profile.createdAt) : null}
      />
      <Line
        k={tSettings('settings.profile.last_login')}
        v={profile?.lastLoginAt ? formatDate(profile.lastLoginAt) : null}
      />
      <Line
        k={tSettings('settings.profile.account_state')}
        v={profile ? tSettings(profile.isActive === false ? 'settings.profile.state_off' : 'settings.profile.state_on') : null}
      />
      <Line k={tSettings('settings.profile.label_phone')} v={profile?.phone ?? null} />
      <Line k={tSettings('settings.profile.label_extension')} v={profile?.extension ?? null} />
      <Line k={tSettings('settings.profile.mail')} v={profile?.email ?? null} />
    </Card>
  );
}

/** 소개 — 새 항목(migration 106). 아무것도 안 채웠으면 카드째 그리지 않는다. */
function IntroCard({ profile }: { profile: ProfileDto | null }) {
  const has = profile?.bio || profile?.nameEn || profile?.birthday || profile?.timezone || profile?.deskLocation;
  if (!has) return null;
  return (
    <Card title={tSettings('settings.profile.card_intro')} icon={<User size={14} aria-hidden />}>
      <Line k={tSettings('settings.profile.label_bio')} v={profile?.bio ?? null} />
      <Line k={tSettings('settings.profile.label_name_en')} v={profile?.nameEn ?? null} />
      <Line k={tSettings('settings.profile.label_desk')} v={profile?.deskLocation ?? null} />
      <Line k={tSettings('settings.profile.label_birthday')} v={profile?.birthday ?? null} />
      <Line k={tSettings('settings.profile.label_timezone')} v={profile?.timezone ?? null} />
    </Card>
  );
}

/** 근무 상태 문구는 상태바·드롭다운이 쓰는 것을 그대로 쓴다(문구를 두 벌로 만들지 않는다). */
function workStatusLabel(status: string): string {
  return tLayout(`layout.workstatus.${status}`);
}
